<script>
import Layout from '@layouts/loginLayout.vue'
import { authMethods, authComputed } from '@state/helpers'
import appConfig from '@src/app.config'
import _ from 'lodash'
import { oktaSessionCookieRedirect } from '@src/api/auth'
import OktaSignIn from '@okta/okta-signin-widget'
import PWAPrompt from 'vue2-ios-pwa-prompt'
import { isIosSafari } from '@src/mixins/isMobileMixin'

export default {
  page: {
    title: 'Log in',
    meta: [
      {
        name: 'description',
        content: `Log in to ${appConfig.title}`,
      },
    ],
  },
  components: { Layout, PWAPrompt },
  mixins: [isIosSafari],
  data() {
    return {
      username: process.env.APP_ENV === 'dev' ? 'Cypress.Test@atesource.info' : '',
      password: process.env.APP_ENV === 'dev' ? 'Motto#e2e!202009' : '',
      authError: null,
      tryingToLogIn: false,
      oktaComponent: null,
    }
  },
  computed: {
    labels() {
      return {
        username: 'Username',
        password: 'Password',
      }
    },

    placeholders() {
      return {
        username: 'Your username',
        password: 'Your password',
      }
    },

    canPressLoginButton() {
      if (!this.username || !this.password) {
        return false
      }

      return !this.tryingToLogIn
    },

    isWrongCredentialsError() {
      return _.isString(this.authError) && this.authError.includes('Invalid username/password combination')
    },
  },
  created() {
    this.toastAdminAuthError()
    this.toastParamMessage()
  },
  mounted() {
    this.$nextTick(() => {
      this.refreshOktaComponent()
    })
  },

  beforeDestroy() {
    this.removeOktaComponent()
  },

  methods: {
    ...authMethods,
    ...authComputed,
    async tryToLogIn({ sessionToken, email }) {
      this.tryingToLogIn = true
      this.authError = null

      try {
        const { sessionToken: responseSessionToken } = await this.logInWithOkta({
          sessionToken,
          email,
        })

        if (responseSessionToken) {
          this.$store.commit('auth/SET_SHOW_REFERRAL_EXPIRED_REMINDER_TOAST', true)
          this.$store.commit('auth/SET_SHOW_PERSONAL_EMAIL_CHANGED_TOAST', true)
          const pathname = this.$route.query.returnUrl
            ? this.$route.query.returnUrl
            : this.$router.resolve({ name: 'home' }).href
          const homeUrl = window.location.origin + pathname.replace('&', '%26')
          localStorage.setItem('successfulLogin', '1')

          await oktaSessionCookieRedirect({
            sessionToken: responseSessionToken,
            redirectUrl: homeUrl,
          })
          return
        }

        await this.$router.push({ name: 'home' })
      } catch (err) {
        const errorMessage = _.get(err, 'response.data.data', 'Server Error. Please try again in a few moments.')
        this.tryingToLogIn = false
        this.authError = errorMessage
        this.toastAuthError()
        this.refreshOktaComponent()
      }
    },

    toastAuthError() {
      this.$bvToast.toast(this.authError, {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-error',
      })
    },

    toastAdminAuthError() {
      if (!this.adminAuthError()) {
        return
      }
      this.$bvToast.toast(this.adminAuthError(), {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-error',
      })
      this.$store.commit('auth/SET_ADMIN_AUTH_ERROR', null)
    },

    toastParamMessage() {
      if (!this.$route.params.message) {
        return
      }
      this.$bvToast.toast(this.$route.params.message, {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-success',
      })
    },

    createOktaComponent() {
      this.oktaComponent = new OktaSignIn({
        baseUrl: process.env.OKTA_URL,
        language: 'en',
        features: {
          hideSignOutLinkInMFA: true,
          rememberMe: false,
          showPasswordToggleOnSignInPage: true,
        },
        i18n: {
          en: {
            'primaryauth.title': 'Login',
            'primaryauth.submit': 'Login',
            'primaryauth.username.placeholder': 'Username',
            'primaryauth.password.placeholder': 'Password',
            'error.username.required': 'Please enter an username',
            'errors.E0000004':
              'Invalid username/password combination. NOTE: Multiple incorrect attempts may result in your account being locked.',
          },
        },
      })
    },

    renderOktaComponent() {
      this.oktaComponent
        .renderEl({ el: '#okta-login-widget' })
        .then((tokens) => {
          const {
            session: { token },
            user: {
              profile: { login },
            },
          } = tokens
          this.tryToLogIn({
            sessionToken: token,
            email: login,
          })
        })
        .catch(function (error) {
          console.log(error)
        })
    },

    removeOktaComponent() {
      try {
        this.oktaComponent.remove()
        this.oktaComponent = null
      } catch (e) {}
    },

    refreshOktaComponent() {
      this.$nextTick(() => {
        this.removeOktaComponent()
        this.createOktaComponent()
        this.renderOktaComponent()
      })
    },

    promptBodyMessage() {
      return this.isIosSafari
        ? 'This website has app functionality. Add it to your home screen to use it in fullscreen.'
        : 'This browser does not support the MottoCenter for Mobile app.'
    },

    promptOnVisit() {
      return !!this.isIosSafari
    },

    promptOnVisitNonIos() {
      return !this.isIosSafari && this.shouldShowPrompt()
    },

    shouldShowPrompt() {
      const iosPwaPromptDate = localStorage.getItem('iosPwaPromptDate')
      if (!iosPwaPromptDate) {
        return true
      }
      const date = new Date()
      const compareCurrentDate = `${date.getFullYear()}-${date.getMonth()}-${date.getDay()}`
      if (compareCurrentDate !== iosPwaPromptDate) {
        localStorage.removeItem('iosPwaPrompt')
        return true
      }
      return false
    },

    promptTitleMessage() {
      return this.isIosSafari ? 'Add to Home Screen' : 'Use Safari to download Motto for Mobile'
    },

    onClosePrompt(event) {
      const date = new Date()
      localStorage.setItem('iosPwaPromptDate', `${date.getFullYear()}-${date.getMonth()}-${date.getDay()}`)
      return true
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.formWrapper">
      <div id="okta-login-widget"></div>
      <BaseLink :to="{ name: 'requestPasswordReset' }" :class="$style.forgotPasswordLink">Forgot my password</BaseLink>
    </div>
    <PWAPrompt
      v-if="promptOnVisit()"
      :on-close="onClosePrompt"
      :copy-body="promptBodyMessage()"
      :copy-title="promptTitleMessage()"
      :times-to-show="9999"
      :permanently-hide-on-dismiss="false" />
    <div v-else-if="promptOnVisitNonIos()" class="nonIosBrowsers">
      <PWAPrompt
        :times-to-show="9999"
        :on-close="onClosePrompt"
        :copy-share-button-label="''"
        :copy-add-home-button-label="''"
        :copy-body="promptBodyMessage()"
        :copy-title="promptTitleMessage()"
        :permanently-hide-on-dismiss="false" />
    </div>
  </Layout>
</template>

<style lang="scss">
@import '@design';

.nonIosBrowsers .pwaPromptShareIcon {
  display: none !important;
}

.nonIosBrowsers .pwaPromptInstructionStep {
  display: none !important;
}

#okta-login-widget {
  max-width: 500px;
  min-height: 500px;
  margin-top: -50px;

  #okta-sign-in {
    margin-top: 0;
    font-family: 'Roboto', sans-serif;

    .o-form-content {
      min-width: 302px;
      padding-bottom: 0;
    }

    .o-form-error-container {
      max-width: 80vw;
    }

    input[type='text'],
    input[type='password'] {
      width: 100%;
      padding: 10px 10px 10px 24px;
      font-family: 'Roboto', sans-serif;
      font-size: 16px;
      color: $primary-blue;
      outline: none;

      &::placeholder {
        /* Chrome, Firefox, Opera, Safari 10.1+ */
        color: $gray;
        opacity: 1;
      }

      &.error {
        color: $secondary-red;
        border: 0.5px $secondary-red solid;
      }
    }

    .primary-auth-form {
      .okta-form-input-field,
      input {
        border-radius: 36px;
      }
    }

    .okta-form-title {
      font-size: 32px;
      color: $primary-blue;
      text-align: left;
    }

    .auth-header {
      border: none;
    }

    .auth-footer {
      display: none;
    }

    .auth-beacon {
      box-shadow: 0 0 0 4px;
    }

    &.auth-container {
      width: 100%;
      background: none;
      border: none;
      box-shadow: none;
    }

    .auth-content {
      max-width: unset;
    }

    .primary-auth-form .o-form-fieldset {
      margin-top: 40px;

      .okta-form-input-field {
        color: $primary-blue;
        border: 0.5px $primary-blue solid;
      }
    }

    .primary-auth-form .o-form-label {
      margin-bottom: 10px;
      font-weight: normal;
      color: $primary-charcoal;
    }

    .factors-dropdown-wrap .dropdown.more-actions .bg-helper {
      box-shadow: none;
    }

    .auth-org-logo {
      display: none;
    }

    #okta-signin-submit {
      height: 48px;
      margin-top: 20px;
      color: $white;
      background: $primary-blue;
      border: none;
      border-radius: 48px;
      transition: background-color 0.3s ease;

      &:disabled {
        cursor: pointer;
        background: $light-gray;
      }

      &:hover:enabled {
        opacity: 0.9;
      }
    }

    .o-form-input-group .link-button:first-child,
    .o-form-input-group .o-form-label-inline:first-child {
      margin-right: 10px;
      border-right-width: 1px;
    }

    .button-show {
      padding-left: 36px;
      opacity: 1;

      &::before {
        top: 8px;
        right: 12px;
        width: 20px;
        height: 16px;
        color: $primary-blue;
        content: '';
        background: url(~@assets/icons/EyeSlash.svg) no-repeat center;
        filter: brightness(0) saturate(100%) invert(15%) sepia(17%) saturate(6452%) hue-rotate(184deg) brightness(93%)
          contrast(104%);
      }
    }

    .button-hide {
      padding-left: 36px;
      opacity: 1;

      &::before {
        top: 8px;
        right: 12px;
        width: 20px;
        height: 16px;
        color: $primary-blue;
        content: '';
        background: url(~@assets/icons/Eye.svg) no-repeat center;
        filter: brightness(0) saturate(100%) invert(15%) sepia(17%) saturate(6452%) hue-rotate(184deg) brightness(93%)
          contrast(104%);
      }
    }
  }
}
</style>

<style lang="scss" module>
@import '@design';

.formWrapper {
  display: flex;
  flex-direction: column;
  margin: auto;

  form {
    width: unset;

    a {
      width: unset;
    }
  }
}

.forgotPasswordLink {
  @media (max-width: 480px) {
    margin-right: 20px;
  }

  @media (min-width: 480px) and (max-width: $lg-breakpoint) {
    margin-right: 40px;
  }

  text-align: right;
}
</style>
